/** My Shopstop V1119 — one responsive movement engine for tablet-only carousels.
 * Desktop and mobile remain grids. Touch/trackpad movement stays browser-native;
 * arrow presses use one predictable, short animation across all converted rails.
 * One viewport of cards moves per press, matching the native storefront rails.
 */
(() => {
  if (customElements.get('ss-tablet-card-carousel')) return;

  const tabletQuery = window.matchMedia('(min-width: 761px) and (max-width: 1100px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const tolerance = 2;
  const duration = 190;

  class SSTabletCardCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ss-tablet-track]');
      this.controls = this.querySelector('[data-ss-tablet-controls]');
      this.previous = this.querySelector('[data-ss-tablet-previous]');
      this.next = this.querySelector('[data-ss-tablet-next]');
      if (!this.track || !this.controls || !this.previous || !this.next) return;

      this.motionFrame = null;
      this.motionTarget = null;
      this.originalMotionStyles = null;
      this.updateFrame = null;
      this.onPrevious = () => this.move(-1);
      this.onNext = () => this.move(1);
      this.onChange = () => this.scheduleUpdate();
      this.onInteraction = () => this.stopMotion();
      this.onLayoutChange = () => {
        this.stopMotion();
        this.scheduleUpdate();
      };

      this.previous.addEventListener('click', this.onPrevious);
      this.next.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onChange, { passive: true });
      this.track.addEventListener('pointerdown', this.onInteraction, { passive: true });
      this.track.addEventListener('wheel', this.onInteraction, { passive: true });

      if (tabletQuery.addEventListener) tabletQuery.addEventListener('change', this.onLayoutChange);
      else tabletQuery.addListener(this.onLayoutChange);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onLayoutChange);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onLayoutChange, { passive: true });
      }
      this.updateControls();
    }

    disconnectedCallback() {
      if (!this.track || !this.controls) return;
      this.previous.removeEventListener('click', this.onPrevious);
      this.next.removeEventListener('click', this.onNext);
      this.track.removeEventListener('scroll', this.onChange);
      this.track.removeEventListener('pointerdown', this.onInteraction);
      this.track.removeEventListener('wheel', this.onInteraction);
      if (tabletQuery.removeEventListener) tabletQuery.removeEventListener('change', this.onLayoutChange);
      else tabletQuery.removeListener(this.onLayoutChange);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      else window.removeEventListener('resize', this.onLayoutChange);
      if (this.updateFrame !== null) cancelAnimationFrame(this.updateFrame);
      this.updateFrame = null;
      this.stopMotion();
    }

    scheduleUpdate() {
      if (this.updateFrame !== null) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.updateControls();
      });
    }

    getScrollStep() {
      const firstCard = this.track.firstElementChild;
      if (!firstCard) return this.track.clientWidth;
      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const cardWidth = firstCard.getBoundingClientRect().width;
      if (cardWidth <= 0) return this.track.clientWidth;
      const visibleCards = Math.max(1, Math.floor((this.track.clientWidth + gap + 0.5) / (cardWidth + gap)));
      return (cardWidth + gap) * visibleCards;
    }

    setMotionStyles() {
      if (this.originalMotionStyles) return;
      this.originalMotionStyles = {
        scrollSnapType: this.track.style.scrollSnapType,
        scrollBehavior: this.track.style.scrollBehavior
      };
      // The CSS snap remains responsible for touch scrolling. Suspend it only
      // during the controlled arrow animation to avoid double easing/settling.
      this.track.style.scrollSnapType = 'none';
      this.track.style.scrollBehavior = 'auto';
    }

    restoreMotionStyles() {
      if (!this.originalMotionStyles) return;
      this.track.style.scrollSnapType = this.originalMotionStyles.scrollSnapType;
      this.track.style.scrollBehavior = this.originalMotionStyles.scrollBehavior;
      this.originalMotionStyles = null;
    }

    stopMotion() {
      if (this.motionFrame !== null) cancelAnimationFrame(this.motionFrame);
      this.motionFrame = null;
      this.motionTarget = null;
      this.restoreMotionStyles();
    }

    move(direction) {
      if (!tabletQuery.matches) return;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (maximum <= tolerance) return;

      const rtl = window.getComputedStyle(this.track).direction === 'rtl';
      const sign = rtl ? -1 : 1;
      const base = this.motionTarget !== null ? this.motionTarget : this.track.scrollLeft;
      const target = sign * Math.min(maximum, Math.max(0, sign * base + direction * this.getScrollStep()));
      const start = this.track.scrollLeft;
      if (Math.abs(target - start) <= tolerance) return;

      if (this.motionFrame !== null) cancelAnimationFrame(this.motionFrame);
      this.motionFrame = null;
      this.motionTarget = target;
      this.setMotionStyles();

      if (reducedMotion.matches) {
        this.track.scrollLeft = target;
        this.stopMotion();
        this.updateControls();
        return;
      }

      const started = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - started) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.track.scrollLeft = start + (target - start) * eased;
        if (progress < 1) {
          this.motionFrame = requestAnimationFrame(tick);
        } else {
          this.motionFrame = null;
          this.motionTarget = null;
          this.restoreMotionStyles();
          this.updateControls();
        }
      };
      this.motionFrame = requestAnimationFrame(tick);
    }

    updateControls() {
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const canMove = tabletQuery.matches && maximum > tolerance;
      this.controls.hidden = !canMove;
      if (canMove) {
        this.track.tabIndex = 0;
        const position = Math.abs(this.motionTarget !== null ? this.motionTarget : this.track.scrollLeft);
        this.previous.disabled = position <= tolerance;
        this.next.disabled = position >= maximum - tolerance;
      } else {
        this.track.removeAttribute('tabindex');
        this.previous.disabled = true;
        this.next.disabled = true;
      }
    }
  }

  customElements.define('ss-tablet-card-carousel', SSTabletCardCarousel);
})();
